// ============================================================
// File d'attente offline — Lot L21 du plan SaaS
// ============================================================
//
// Stocke en IndexedDB les opérations write tentées sans réseau.
// Au retour du réseau, on rejoue chaque op via axios avec un header
// `Idempotency-Key` (UUID v4) — le serveur garantit qu'une op déjà
// passée n'est pas dupliquée.
//
// API :
//   await enqueue(op)            → ajoute une op { method, url, data, headers }
//   await flush(api)             → rejoue toutes les ops (renvoie un récap)
//   await listPending()          → liste les ops en attente
//   await purger(id)             → supprime une op (utilisé après succès)
//   await pendingCount()         → nombre d'ops en file
//
// Stockage : IndexedDB "sv_offline_queue" / store "operations"
// ============================================================

const DB_NAME = 'sv_offline_queue';
const DB_VERSION = 1;
const STORE = 'operations';

function uuidV4() {
  // RFC 4122 v4 minimal
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(store, mode) {
  return openDb().then(db => db.transaction(STORE, mode).objectStore(STORE));
}

export async function enqueue(op) {
  const item = {
    id: uuidV4(),
    idempotencyKey: uuidV4(),
    method: (op.method || 'POST').toUpperCase(),
    url: op.url,
    data: op.data || null,
    headers: op.headers || {},
    createdAt: Date.now(),
    tentatives: 0,
    derniere_erreur: null,
  };
  const store = await tx(STORE, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.add(item);
    req.onsuccess = () => resolve(item);
    req.onerror = () => reject(req.error);
  });
}

export async function listPending() {
  const store = await tx(STORE, 'readonly');
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result.sort((a, b) => a.createdAt - b.createdAt));
    req.onerror = () => reject(req.error);
  });
}

export async function pendingCount() {
  const store = await tx(STORE, 'readonly');
  return new Promise((resolve, reject) => {
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function purger(id) {
  const store = await tx(STORE, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function updateOp(id, patch) {
  const store = await tx(STORE, 'readwrite');
  return new Promise((resolve, reject) => {
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const item = getReq.result;
      if (!item) return resolve(null);
      Object.assign(item, patch);
      const putReq = store.put(item);
      putReq.onsuccess = () => resolve(item);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

/**
 * Rejoue toutes les opérations en attente.
 * @param {axios.Instance} api  - instance axios (déjà configurée avec auth)
 * @returns {Promise<{succes: number, echecs: number, total: number}>}
 */
export async function flush(api) {
  const pending = await listPending();
  let succes = 0;
  let echecs = 0;
  for (const op of pending) {
    try {
      await api.request({
        method: op.method,
        url: op.url,
        data: op.data,
        headers: { ...op.headers, 'Idempotency-Key': op.idempotencyKey },
      });
      await purger(op.id);
      succes++;
    } catch (err) {
      // 4xx → l'op est définitivement invalide, on la supprime
      if (err.response?.status >= 400 && err.response?.status < 500) {
        await purger(op.id);
        echecs++;
      } else {
        await updateOp(op.id, {
          tentatives: (op.tentatives || 0) + 1,
          derniere_erreur: err.message || 'inconnu',
        });
        echecs++;
      }
    }
  }
  return { succes, echecs, total: pending.length };
}

export async function vider() {
  const store = await tx(STORE, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
